<script setup lang="ts">
import AppRawButton from '../appRawButton/AppRawButton.vue';
import type {
  AppSvgIconSize,
  AppSvgIconType,
} from '../appSvgIcon/AppSvgIcon.model';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppLinkButtonFontType,
  AppLinkButtonSize,
} from './AppLinkButton.model';

interface AppLinkButtonProps {
  text: string;
  size: AppLinkButtonSize;
  font?: AppLinkButtonFontType;
  bold?: boolean;
  href?: string;
  rightIcon?: AppSvgIconType;
  leftIcon?: AppSvgIconType;
  iconSize?: AppSvgIconSize;
  underline?: boolean;
  iconAriaLabel?: string;
}

const props = withDefaults(defineProps<AppLinkButtonProps>(), {
  iconSize: '24',
  bold: false,
  underline: true,
  font: 'secondary',
});

interface AppLinkButtonEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<AppLinkButtonEmits>();
</script>

<template>
  <a
    v-if="props.href"
    :href="props.href"
    target="_blank"
    rel="noopener noreferrer"
    :class="{
      'app-link': true,
      [props.size]: true,
      [props.font]: true,
      bold: props.bold,
      'no-underline': !props.underline,
    }"
  >
    {{ props.text }}
  </a>

  <AppRawButton
    v-else
    :class="{
      'app-link-button': true,
      [props.size]: true,
      [props.font]: true,
      bold: props.bold,
      'has-icon': props.rightIcon || props.leftIcon,
      'no-underline': !props.underline,
    }"
    @click="emit('click')"
  >
    <AppSvgIcon
      v-if="props.leftIcon"
      :name="props.leftIcon"
      :size="props.iconSize"
      :aria-label="props.iconAriaLabel"
    />
    {{ props.text }}
    <AppSvgIcon
      v-if="props.rightIcon"
      :name="props.rightIcon"
      :size="props.iconSize"
      :aria-label="props.iconAriaLabel"
    />
  </AppRawButton>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;

.app-link,
.app-link-button {
  background-color: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-decoration: underline;
  color: var(--color-neutral-70);
  text-align: left;
  align-items: center;

  &.s {
    @include font-link-s-light;
  }

  &.s.bold {
    @include font-link-s-bold;
  }

  &.m {
    @include font-link-m-light;
  }

  &.m.bold {
    @include font-label-m-bold;
  }

  &.m.bold.primary {
    @include font-title-label-m;
  }

  &.l {
    @include font-link-l-light;
  }

  &.l.bold {
    @include font-label-l-bold;
  }

  &.xl {
    @include font-link-xl-light;
  }

  &.xl.bold {
    @include font-label-xl-bold;
  }

  &.has-icon {
    display: inline-flex;
    gap: var(--spacing-8);
  }

  &.no-underline {
    text-decoration: none;
  }
}
</style>
